export interface ServiceSegment {
  type: 'work' | 'pause';
  duration: number; // in minutes
  start_offset?: number; // Minutes from service start. undefined = sequential (backward compat)
}

/**
 * Represents a booking returned by the get-availability edge functions.
 * Used by conflict detection logic on the client side.
 */
export interface AvailabilityBooking {
  appointment_time: string;  // "HH:MM" format
  duration: number;          // in minutes
  segments?: ServiceSegment[] | null;  // optional segments for segment-aware conflict checking
  /**
   * Plage bloquée (fermeture de l'employé ou du salon), marquée par
   * get-availability et get-availability-batch. `isSlotBlocked` la juge sur
   * l'empan ENTIER de la tranche demandée, sans tampon, comme le serveur.
   * Absent : un rendez-vous.
   */
  kind?: 'blocked';
}

export interface Subsection {
  id: string;
  name: string;
  name_en?: string | null;
  section_id: string;
  display_order?: number | null;
}

export interface Service {
  id: string;
  name: string;
  name_en?: string | null;
  short_name?: string | null;
  duration: number | null;
  price: number | null;
  description: string;
  description_en?: string | null;
  section?: string | null;
  subsection_id?: string | null;
  subsection?: Subsection | null;
  bookable_online?: boolean;
  segments?: ServiceSegment[] | null;
  price_type?: 'fixed' | 'variable' | 'starting_from';
  duration_type?: 'fixed' | 'variable' | 'starting_from';
  // Database field - source of truth for employee pricing
  has_employee_prices?: boolean;
  // Employee pricing fields (populated when fetching services with pricing info)
  hasEmployeePricing?: boolean; // Computed field (derived from has_employee_prices)
  minPrice?: number | null; // Minimum price across all employees (for "À partir de" display)
  effectivePrice?: number | null; // Resolved price for selected employee
  // Database field - source of truth for employee durations
  has_employee_durations?: boolean;
  // Employee duration fields (populated when fetching services with duration info)
  hasEmployeeDurations?: boolean; // Computed field (derived from has_employee_durations)
  minDuration?: number | null; // Minimum duration across all employees (for "Dès X min" display)
  maxDuration?: number | null; // Maximum duration across all employees (to check if durations vary)
  effectiveDuration?: number | null; // Resolved duration for selected employee
  display_order?: number | null;
  // Surcharge d'acompte propre au service (lot 3, I-2). `anon` a le SELECT sur
  // ces trois colonnes, et le tunnel public fait un `select("*")` : elles
  // arrivent déjà jusqu'aux composants, il ne leur manquait que d'être
  // déclarées ici. La résolution du montant reste SERVEUR ; le front ne s'en
  // sert que pour annoncer une estimation.
  deposit_override_enabled?: boolean | null;
  deposit_type?: 'percentage' | 'fixed' | null;
  deposit_value?: number | null;
}

/**
 * Represents an employee-specific price override for a service.
 * When set, this price takes precedence over the service's base price for the specified employee.
 */
export interface ForfaitItem {
  id?: string;
  position: number;
  item_type: 'service' | 'pause';
  service_id: string | null;
  hairdresser_id: string | null;   // null = "au choix"
  duration: number | null;
  price_override: number | null;
}

export interface Forfait {
  id: string;
  salon_id: string;
  name: string;
  name_en: string | null;
  description: string | null;
  description_en: string | null;
  is_active: boolean;
  display_order: number;
  items: ForfaitItem[];
}

export interface ServiceEmployeePrice {
  id?: string;
  service_id: string;
  hairdresser_id: string;
  price: number;
  created_at?: string;
  updated_at?: string;
  // Optional: populated via join for display purposes
  hairdresser?: {
    id: string;
    name: string;
    level?: number | null;
  };
}

/**
 * Represents an employee-specific duration override for a service.
 * When set, this duration takes precedence over the service's base duration for the specified employee.
 */
export interface ServiceEmployeeDuration {
  id?: string;
  service_id: string;
  hairdresser_id: string;
  duration: number; // in minutes
  created_at?: string;
  updated_at?: string;
  // Optional: populated via join for display purposes
  hairdresser?: {
    id: string;
    name: string;
    level?: number | null;
  };
}

/**
 * Represents a service linked to an appointment in the junction table.
 * Contains denormalized data frozen at booking time for historical accuracy.
 */
export interface AppointmentService {
  id?: string;
  appointment_id?: string;
  service_id: string;
  service_name: string;
  service_price: number | null;
  service_duration: number;
  price_type?: 'fixed' | 'variable' | 'starting_from';
  service_order: number;
  // Minutes from appointment start when this service begins (enables gaps/pauses between services)
  start_offset_minutes?: number;
  // Denormalized segments frozen at booking time (proportionally scaled to employee's effective duration)
  segments?: ServiceSegment[] | null;
  // Status of individual service: confirmed, cancelled, or rescheduled
  // Cancelled services appear red on calendar and can be deleted to free the slot
  // Rescheduled services appear blue and link to a new standalone appointment
  status?: 'confirmed' | 'cancelled' | 'rescheduled';
  // When status='rescheduled', links to the new standalone appointment
  rescheduled_to_appointment_id?: string;
  created_at?: string;
  // Optional: full service object for UI display purposes
  service?: Service;
  // Per-service employee assignment (multi-service multi-employee bookings)
  hairdresser_id?: string;
  hairdresser_name?: string;
}

/**
 * AppointmentServiceItem - Stricter version of AppointmentService used in UI components.
 * Requires id to be present (used when services are already persisted to DB).
 */
export interface AppointmentServiceItem {
  id: string;
  service_id?: string;
  service_name: string;
  service_price: number | null;
  service_duration: number;
  service_order: number;
  // Minutes from appointment start when this service begins (enables gaps/pauses between services)
  start_offset_minutes?: number;
  // Denormalized segments frozen at booking time (proportionally scaled to employee's effective duration)
  segments?: ServiceSegment[] | null;
  // Optional: override hairdresser for this specific service (for multi-employee appointments)
  hairdresser_id?: string;
  hairdresser_name?: string;
  // Status of individual service: confirmed, cancelled, or rescheduled
  // Cancelled services appear red on calendar and can be deleted to free the slot
  // Rescheduled services appear blue and link to a new standalone appointment
  status?: 'confirmed' | 'cancelled' | 'rescheduled';
  // When status='rescheduled', links to the new standalone appointment
  rescheduled_to_appointment_id?: string;
  // Forfait (package) link: set when this row was booked as part of a forfait.
  // Carried through the owner edit flow so the save preserves the bundle.
  forfait_id?: string | null;
  forfait_item_id?: string | null;
  // Joined from services table for segment display on calendar (fallback for old
  // data) and for localized service-name display in the client booking history.
  services?: {
    segments: ServiceSegment[] | null;
    name?: string | null;
    name_en?: string | null;
  } | null;
}

/**
 * Represents a service linked to a recurring series in the junction table.
 */
export interface RecurringSeriesService {
  id?: string;
  series_id?: string;
  service_id: string;
  service_name: string;
  service_price: number | null;
  service_duration: number;
  price_type?: 'fixed' | 'variable' | 'starting_from';
  service_order: number;
  created_at?: string;
  service?: Service;
}

export interface TimeSlot {
  time: string;
  available: boolean;
}

export interface CustomerInfo {
  name: string;
  email: string;
  phone: string;
  notes?: string;
}

export interface Appointment {
  /**
   * Identifiant du rendez-vous réellement créé (I-12).
   *
   * Absent tant qu'aucun rendez-vous n'existe (récapitulatif avant
   * confirmation), et absent aussi sur une série récurrente : un acompte par
   * occurrence n'est pas conçu, et en réclamer un là serait inventer un
   * comportement. Le panneau d'acompte reste alors masqué.
   */
  appointmentId?: string;
  // Legacy single service (kept for backward compatibility)
  service: Service;
  // New: array of services for multi-service appointments
  services?: AppointmentService[];
  // Computed totals for multi-service appointments
  totalDuration?: number;
  totalPrice?: number;
  date: Date;
  time: string;
  customer: CustomerInfo;
  hairdresser?: {
    name: string;
  };
  /**
   * Noms des employés RÉELLEMENT attribués, dans l'ordre des services (tâche
   * 18, spec 5.15 : « Votre rendez-vous avec Walid »). Lu par l'écran de succès
   * seul, qui nettoie, retire les vides et les doublons. Facultatif : absent ou
   * sans nom utilisable, l'écran n'affiche aucune phrase.
   * ⛔ Toujours l'attribution, jamais l'intention (« N'importe qui ») ni
   * `selectedHairdresser` : le nom n'existe qu'à partir de l'instant où il est
   * vrai (décision D2).
   */
  assignedEmployeeNames?: string[];
  /**
   * Lot 4, tâche 13 : emplacement où le rendez-vous a RÉELLEMENT été écrit.
   * Posé en mode « n'importe quel emplacement » seulement (l'écran de succès y
   * affiche son adresse) ; absent sinon, la copie figée reste celle d'avant.
   */
  salon_id?: string;
}

// ============================================================================
// Constants
// ============================================================================

/**
 * Maximum number of services allowed per appointment
 */
export const MAX_SERVICES_PER_APPOINTMENT = 5;

/**
 * Default service duration in minutes when no duration is specified
 * Used as ultimate fallback to prevent undefined behavior
 */
export const DEFAULT_SERVICE_DURATION = 60;

/**
 * Default service price when no price is specified
 * Used as fallback for services with missing price data
 */
export const DEFAULT_SERVICE_PRICE = 0;

// ============================================================================
// Validation Functions
// ============================================================================

/**
 * Validates and returns a service price, logging warnings for invalid data
 *
 * @param price - The price to validate
 * @param priceType - The price type (fixed, variable, starting_from)
 * @param serviceName - Optional service name for debugging
 * @returns The validated price or DEFAULT_SERVICE_PRICE if invalid
 */
export const validateServicePrice = (
  price: number | null | undefined,
  priceType: string | null | undefined,
  serviceName?: string
): number => {
  // Null/undefined handling with warning for fixed price services
  if (price === null || price === undefined) {
    if (priceType === 'fixed') {
      console.warn(`[validateServicePrice] Service "${serviceName || 'unknown'}" has fixed price_type but null/undefined price`);
    }
    return DEFAULT_SERVICE_PRICE;
  }

  // Type validation
  if (typeof price !== 'number' || isNaN(price) || !isFinite(price)) {
    console.warn(`[validateServicePrice] Invalid price for "${serviceName || 'unknown'}": ${price}`);
    return DEFAULT_SERVICE_PRICE;
  }

  return price;
};

/**
 * Validates and returns a service duration, logging warnings for invalid data
 *
 * @param duration - The duration to validate (in minutes)
 * @param durationType - The duration type (fixed, variable, starting_from)
 * @param serviceName - Optional service name for debugging
 * @returns The validated duration or DEFAULT_SERVICE_DURATION if invalid
 */
export const validateServiceDuration = (
  duration: number | null | undefined,
  durationType: string | null | undefined,
  serviceName?: string
): number => {
  // Null/undefined handling with warning for fixed duration services
  if (duration === null || duration === undefined) {
    if (durationType === 'fixed') {
      console.warn(`[validateServiceDuration] Service "${serviceName || 'unknown'}" has fixed duration_type but null/undefined duration`);
    }
    return DEFAULT_SERVICE_DURATION;
  }

  // Type and range validation
  if (typeof duration !== 'number' || isNaN(duration) || !isFinite(duration) || duration <= 0) {
    console.warn(`[validateServiceDuration] Invalid duration for "${serviceName || 'unknown'}": ${duration}`);
    return DEFAULT_SERVICE_DURATION;
  }

  return duration;
};

// ============================================================================
// Multi-Service Duration Calculation
// ============================================================================

/**
 * Interface for items that can be used in duration calculation
 * Supports both services and pauses (gaps between services)
 */
export interface DurationCalculationItem {
  type: 'service' | 'pause';
  duration?: number;
  variableDuration?: number;
  effectiveDuration?: number | null;
  service?: {
    duration?: number | null;
    duration_type?: string;
    name?: string;
  };
}

/**
 * Calculate total duration for multi-service bookings
 * Uses consistent priority order: variableDuration > effectiveDuration > service.duration > DEFAULT
 *
 * @param items - Array of services and pauses
 * @param includePauses - Whether to include pause durations in total (default: true)
 * @returns Total duration in minutes
 */
export const calculateMultiServiceTotalDuration = (
  items: DurationCalculationItem[],
  includePauses: boolean = true
): number => {
  return items.reduce((total, item) => {
    if (item.type === 'pause') {
      return includePauses ? total + (item.duration || 0) : total;
    }

    // Service: priority order for duration
    if (item.variableDuration && item.variableDuration > 0) {
      return total + item.variableDuration;
    }
    if (item.effectiveDuration && item.effectiveDuration > 0) {
      return total + item.effectiveDuration;
    }
    if (item.service?.duration && item.service.duration > 0) {
      return total + item.service.duration;
    }

    // Variable services without explicit duration return 0 (handled by validation)
    if (item.service?.duration_type === 'variable') {
      return total; // Don't add default - this should trigger validation error
    }

    return total + DEFAULT_SERVICE_DURATION;
  }, 0);
};

// ============================================================================
// Multi-Service Helper Functions
// ============================================================================

/**
 * Calculate total duration from an array of services
 */
export const calculateTotalDuration = (services: Service[]): number => {
  return services.reduce((total, service) => {
    return total + (service.duration || 0);
  }, 0);
};

/**
 * Calculate total price from an array of services
 * Returns null if any service has variable pricing
 */
export const calculateTotalPrice = (services: Service[]): number | null => {
  const hasVariable = services.some(s => s.price_type === 'variable');
  if (hasVariable) return null;

  return services.reduce((total, service) => {
    return total + (service.price || 0);
  }, 0);
};

/**
 * Check if any service in the array has variable pricing
 */
export const hasVariablePrice = (services: Service[]): boolean => {
  return services.some(s => s.price_type === 'variable');
};

/**
 * Check if any service in the array has variable duration
 */
export const hasVariableDuration = (services: Service[]): boolean => {
  return services.some(s => s.duration_type === 'variable');
};

/**
 * Calculate total duration using employee-specific resolved durations
 * Falls back to base duration if no resolved duration is available
 */
export const calculateEffectiveTotalDuration = (
  services: Service[],
  resolvedDurations: Map<string, number>
): number => {
  return services.reduce((total, service) => {
    const effectiveDuration = resolvedDurations.get(service.id) ?? service.duration ?? 60;
    return total + effectiveDuration;
  }, 0);
};

/**
 * Calculate total price using employee-specific resolved prices
 * Returns null if any service has variable pricing
 * Falls back to base price if no resolved price is available
 */
export const calculateEffectiveTotalPrice = (
  services: Service[],
  resolvedPrices: Map<string, number>
): number | null => {
  const hasVariable = services.some(s => s.price_type === 'variable');
  if (hasVariable) return null;

  return services.reduce((total, service) => {
    const effectivePrice = resolvedPrices.get(service.id) ?? service.price ?? 0;
    return total + effectivePrice;
  }, 0);
};

/**
 * Format duration for display with support for "starting_from" type
 * e.g., 90 -> "1h 30min", with starting_from: "Dès 1h 30min"
 */
export const formatServiceDuration = (
  service: Service,
  resolvedDuration?: number
): string => {
  const effectiveDuration = resolvedDuration ?? service.duration;

  if (service.duration_type === 'starting_from') {
    const minDuration = service.minDuration ?? effectiveDuration;
    return `Dès ${formatDuration(minDuration ?? 60)}`;
  }
  if (service.duration_type === 'variable') {
    return 'Durée variable';
  }
  return formatDuration(effectiveDuration ?? 60);
};

/**
 * Convert Service array to AppointmentService array for database insertion
 * Note: Does NOT include the full service object - that's for UI only
 */
export const servicesToAppointmentServices = (
  services: Service[],
  appointmentId?: string
): Omit<AppointmentService, 'id' | 'created_at' | 'service'>[] => {
  return services.map((service, index) => ({
    appointment_id: appointmentId,
    service_id: service.id,
    service_name: service.name,
    service_price: service.price,
    service_duration: service.duration || 60,
    price_type: service.price_type || 'fixed',
    service_order: index,
  }));
};

/**
 * Convert Service array to AppointmentService array for UI display
 * Includes full service object for accessing section info
 * @param services - Array of services to convert
 * @param resolvedDurations - Optional map of service ID to employee-specific duration
 * @param resolvedPrices - Optional map of service ID to employee-specific price
 */
export const servicesToAppointmentServicesForUI = (
  services: Service[],
  resolvedDurations?: Map<string, number>,
  resolvedPrices?: Map<string, number>
): AppointmentService[] => {
  return services.map((service, index) => ({
    service_id: service.id,
    service_name: service.name,
    service_price: resolvedPrices?.get(service.id) ?? service.price,
    service_duration: resolvedDurations?.get(service.id) ?? service.duration ?? 60,
    price_type: service.price_type || 'fixed',
    service_order: index,
    service: service,
  }));
};

/**
 * Format duration in minutes to human-readable string
 * e.g., 90 -> "1h 30min", 60 -> "1h", 45 -> "45min"
 */
export const formatDuration = (minutes: number): string => {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;

  if (hours === 0) return `${mins}min`;
  if (mins === 0) return `${hours}h`;
  return `${hours}h ${mins}min`;
};

// ============================================================================
// Helper Functions for AppointmentService[] (from junction table)
// ============================================================================

/**
 * Calculate total duration from an array of AppointmentService
 * Use when working with appointment_services junction table data
 */
export const calculateAppointmentServicesTotalDuration = (
  services: AppointmentService[]
): number => {
  return services.reduce((total, service) => {
    return total + (service.service_duration || 0);
  }, 0);
};

/**
 * Calculate total price from an array of AppointmentService
 * Returns null if any service has variable pricing
 * Uses validateServicePrice for proper validation and logging
 * Use when working with appointment_services junction table data
 */
export const calculateAppointmentServicesTotalPrice = (
  services: AppointmentService[]
): number | null => {
  const hasVariable = services.some(s => s.price_type === 'variable');
  if (hasVariable) return null;

  return services.reduce((total, service) => {
    const validPrice = validateServicePrice(
      service.service_price,
      service.price_type,
      service.service_name
    );
    return total + validPrice;
  }, 0);
};
